import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { LocalizationService } from '../../core/localization/localization.service';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { FinancialAccountSelectorComponent } from '../../shared/design-system/financial-account-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CashboxRecord, CreateCashboxPayload, FinanceApiService, UpdateCashboxPayload } from './finance-api.service';

@Component({
  standalone: true,
  selector: 'ain-finance-cashbox-detail',
  imports: [AinPageComponent, AinStateComponent, RouterLink, FormsModule, ButtonModule, InputTextModule, CustomerSelectorComponent, FinancialAccountSelectorComponent],
  template: `
    <ain-page [title]="isCreateMode() ? t('finance.cashboxes.form.createTitle') : t('finance.cashboxes.form.editTitle')" [description]="t('finance.cashboxes.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/financial/payments/cashboxes']">{{ t('catalog.action.backToList') }}</a>
          <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="save()"></button>
        </div>

        <section class="card">
          <div class="grid">
            <label>
              <span>{{ t('finance.cashboxes.fields.name') }}</span>
              <input pInputText type="text" [(ngModel)]="form.name" />
            </label>
            <label>
              <span>{{ t('finance.cashboxes.fields.customerId') }}</span>
              <ain-customer-selector [(ngModel)]="form.customerId"></ain-customer-selector>
            </label>
            <label class="wide">
              <span>{{ t('finance.cashboxes.fields.accountingAccountId') }}</span>
              <ain-financial-account-selector [(ngModel)]="form.accountingAccountId"></ain-financial-account-selector>
            </label>
          </div>
        </section>

        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .back-link { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; }
    .grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
    .wide { grid-column: span 2; }
    label { display: grid; gap: 6px; }
    .message.error { color: #b91c1c; }
    @media (max-width: 760px) {
      .grid { grid-template-columns: 1fr; }
      .wide { grid-column: span 1; }
    }
  `]
})
export class FinanceCashboxDetailComponent implements OnInit {
  private readonly api = inject(FinanceApiService);
  private readonly localization = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly errorMessage = signal('');
  readonly isCreateMode = signal(false);
  readonly current = signal<CashboxRecord | null>(null);

  readonly form = {
    name: '',
    customerId: '',
    accountingAccountId: ''
  };

  ngOnInit(): void {
    const cashboxId = this.route.snapshot.paramMap.get('cashboxId');
    if (!cashboxId || cashboxId === 'new') {
      this.isCreateMode.set(true);
      this.loading.set(false);
      return;
    }

    this.api.getAccountingCashbox(cashboxId).subscribe({
      next: row => {
        this.current.set(row);
        this.patchForm(row);
        this.loading.set(false);
      },
      error: () => {
        this.errorMessage.set(this.t('common.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  save(): void {
    this.errorMessage.set('');
    if (!this.form.name.trim() || !this.form.customerId.trim()) {
      this.errorMessage.set(this.t('finance.cashboxes.messages.invalidForm'));
      return;
    }

    this.saving.set(true);
    if (this.isCreateMode()) {
      const payload: CreateCashboxPayload = {
        number: '',
        idempotencyKey: this.createIdempotencyKey(),
        amount: 0,
        currencyCode: 'IRR',
        name: this.form.name.trim(),
        customerId: this.form.customerId.trim(),
        accountingAccountId: this.form.accountingAccountId.trim()
      };
      this.api.createAccountingCashbox(payload).subscribe({
        next: created => {
          this.saving.set(false);
          void this.router.navigate(['/financial/payments/cashboxes', created.id]);
        },
        error: () => {
          this.errorMessage.set(this.t('finance.cashboxes.messages.saveFailed'));
          this.saving.set(false);
        }
      });
      return;
    }

    const current = this.current();
    if (!current) {
      this.errorMessage.set(this.t('finance.cashboxes.messages.noSelection'));
      this.saving.set(false);
      return;
    }

    const payload: UpdateCashboxPayload = {
      name: this.form.name.trim(),
      customerId: this.form.customerId.trim(),
      accountingAccountId: this.form.accountingAccountId.trim()
    };
    this.api.updateAccountingCashbox(current.id, payload).subscribe({
      next: updated => {
        this.current.set(updated);
        this.patchForm(updated);
        this.saving.set(false);
      },
      error: () => {
        this.errorMessage.set(this.t('finance.cashboxes.messages.saveFailed'));
        this.saving.set(false);
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private patchForm(row: CashboxRecord): void {
    this.form.name = row.name;
    this.form.customerId = row.customerId;
    this.form.accountingAccountId = row.accountingAccountId;
  }

  private createIdempotencyKey(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID().replace(/-/g, '');
    }

    return `${Date.now()}${Math.floor(Math.random() * 1000000)}`;
  }
}
